var _jsxFileName = "/codebuild/output/src1346489397/src/bitbucket.org/newyuinc/adc-polaris-web/src/components/consents/LibreAssistConsent.tsx";
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IconBack, IconClose, InformationalScreen, LoadingModal, Stack, Text } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ErrorCode } from 'Constants';
import axios from 'axios';
import { useApiError } from 'Hooks';
import { Button, MainContainer, OptInOut, RichText } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage, getNestedValue, hasPendingConsent } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const ASSIST_CMS_CONFIG = {
  US: {
    titleKey: 'Consents.content.assistDataUse.primaryText',
    bodyTextKey: 'Consents.content.assistDataUse.bodyText',
    acceptButtonKey: 'Global.microcopy.common.accept',
    declineModalTitleKey: 'Consents.modals.usAssistConsent.title',
    declineModalBodyKey: 'Consents.modals.usAssistConsent.body',
    acceptButtonLabelKey: 'Global.microcopy.common.accept',
    reviewConsentModalKey: 'Consents.modals.reviewNoticeDialog.body',
    positiveTagKey: 'Global.microcopy.common.accepted',
    positiveOptionKey: 'Global.microcopy.common.accept'
  },
  OUS: {
    titleKey: 'AssistConsentOus.title',
    bodyTextKey: 'AssistConsentOus.content.notice.bodyText',
    acceptButtonKey: 'Global.microcopy.common.optIn',
    declineModalTitleKey: 'AssistConsentOus.modals.ousDecline.title',
    declineModalBodyKey: 'AssistConsentOus.modals.ousDecline.body',
    acceptButtonLabelKey: 'Global.microcopy.common.optIn',
    reviewConsentModalKey: 'AssistConsentOus.modals.reviewConsentDialog.body',
    positiveTagKey: 'Global.microcopy.common.optedIn',
    positiveOptionKey: 'Global.microcopy.common.optIn'
  }
};
const mapStateToProps = ({
  env: {
    osType,
    osVersion,
    app,
    appVersion,
    phoneLanguage
  },
  auth,
  nav: {
    query
  },
  config: {
    documentUrls,
    documentPaths
  }
}) => {
  var _documentPaths$cms;
  return {
    osType,
    osVersion,
    app,
    appVersion,
    auth,
    isAccountProfile: Boolean(query.accountProfile),
    cmsStrapiDomain: documentUrls.cmsStrapi,
    assistCmsPaths: (_documentPaths$cms = documentPaths.cms) === null || _documentPaths$cms === void 0 ? void 0 : _documentPaths$cms.assistlibre,
    phoneLanguage
  };
};
const connector = connect(mapStateToProps);
const LibreAssistConsent = ({
  osType,
  osVersion,
  app,
  appVersion,
  auth,
  isAccountProfile,
  cmsStrapiDomain,
  assistCmsPaths,
  phoneLanguage
}) => {
  var _auth$include$patient;
  const [loading, setLoading] = useState(true);
  const [requiresConsentReview, setRequiresConsentReview] = useState(false);
  const [assistContentTitleKey, setAssistContentTitleKey] = useState('');
  const [assistContentBodyTextKey, setAssistContentBodyTextKey] = useState('');
  const {
    showApiErrorModal,
    showNetworkErrorModal
  } = useApiError();
  const showConsentActions = requiresConsentReview || !isAccountProfile;
  const isUS = auth.include.patient.country === 'US';
  const cmsConfig = isUS ? ASSIST_CMS_CONFIG.US : ASSIST_CMS_CONFIG.OUS;
  const handleFetchingError = useCallback(() => {
    if (isAccountProfile) {
      showNetworkErrorModal();
      mediator.publish('router:back');
      return;
    }
    setAssistContentTitleKey(cmsConfig.titleKey);
    setAssistContentBodyTextKey(cmsConfig.bodyTextKey);
  }, [cmsConfig, isAccountProfile, showNetworkErrorModal]);
  const fetchAssistConsentText = useCallback(async () => {
    try {
      const assistCmsUrl = getDocumentPathFromLanguage(assistCmsPaths, phoneLanguage);
      const assistCmsFullDomain = `${cmsStrapiDomain}${assistCmsUrl}`;
      const response = await axios.get(assistCmsFullDomain);
      const data = response.data;
      const cmsTitle = getNestedValue(data, cmsConfig.titleKey);
      const cmsBodyText = getNestedValue(data, cmsConfig.bodyTextKey);
      if (!cmsTitle || !cmsBodyText) {
        handleFetchingError();
        setLoading(false);
        return;
      }
      setAssistContentTitleKey(cmsTitle);
      setAssistContentBodyTextKey(cmsBodyText);
      if (hasPendingConsent(auth.include.patient.consents.assist)) {
        store.dispatch({
          type: SHOW_MODAL,
          modalProps: {
            details: i18n.t(cmsConfig.reviewConsentModalKey),
            testID: 'ReviewConsent'
          }
        });
        setRequiresConsentReview(true);
      }
    } catch {
      handleFetchingError();
    } finally {
      setLoading(false);
    }
  }, [assistCmsPaths, phoneLanguage, cmsStrapiDomain, cmsConfig.titleKey, cmsConfig.bodyTextKey, cmsConfig.reviewConsentModalKey, auth.include.patient.consents.assist, handleFetchingError]);
  useEffect(() => {
    fetchAssistConsentText();
  }, [fetchAssistConsentText]);
  const handleConsentWrapper = useCallback(async action => {
    try {
      const resp = await handleConsent(auth.account_id, action, osType, osVersion, app, appVersion, 'assist');
      const updatedAuthSession = {
        ...auth,
        include: {
          patient: {
            ...auth.include.patient,
            consents: {
              ...auth.include.patient.consents,
              assist: {
                ...resp
              }
            }
          }
        }
      };
      let consentLocation = isAccountProfile ? 'profile' : 'tutorial';
      consentLocation = requiresConsentReview ? 'runtime' : consentLocation;
      notifyApp('adc-webview:consents', {
        id: 'assist',
        action,
        location: consentLocation
      });
      store.dispatch({
        type: AUTH_SESSION,
        authSession: updatedAuthSession
      });
    } catch (err) {
      const error = err;
      error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
    }
  }, [app, appVersion, auth, isAccountProfile, osType, osVersion, requiresConsentReview, showApiErrorModal, showNetworkErrorModal]);
  const handleSubmitAction = useCallback(async action => {
    try {
      setLoading(true);
      await handleConsentWrapper(action);
      setRequiresConsentReview(false);
      if (isAccountProfile) {
        // Ends the loading state if the user is on the account profile page
        setLoading(false);
        return;
      }

      // This timeout is added to give the app time to redirect the user back.
      setTimeout(() => {
        setLoading(false);
      }, APP_RETURN_TIMEOUT);
    } catch {
      setLoading(false);
    }
  }, [handleConsentWrapper, isAccountProfile]);
  const onDeclineButtonClick = useCallback(() => {
    store.dispatch({
      type: SHOW_MODAL,
      modalProps: {
        details: /*#__PURE__*/React.createElement(Stack, {
          gap: 4,
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 252,
            columnNumber: 11
          }
        }, /*#__PURE__*/React.createElement(Text, {
          color: "$text.100",
          type: "$bodyBase.default",
          testID: "LibreAssist.declineModalTitle",
          marginBottom: "$4",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 253,
            columnNumber: 13
          }
        }, i18n.t(cmsConfig.declineModalTitleKey)), /*#__PURE__*/React.createElement(RichText, {
          text: i18n.t(cmsConfig.declineModalBodyKey),
          testID: "LibreAssist.declineModalBodyText",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 261,
            columnNumber: 13
          }
        })),
        primaryAction: {
          label: i18n.t('Global.microcopy.common.declineAnyway'),
          onPress: () => {
            handleSubmitAction('decline');
            store.dispatch({
              type: CLOSE_MODAL
            });
          },
          destructive: true
        },
        secondaryAction: {
          label: i18n.t('Global.microcopy.common.back'),
          onPress: () => {
            store.dispatch({
              type: CLOSE_MODAL
            });
          }
        },
        buttonLayout: 'column',
        testID: 'LibreAssist'
      }
    });
  }, [cmsConfig.declineModalBodyKey, cmsConfig.declineModalTitleKey, handleSubmitAction]);
  const handleContentLinkClick = useCallback(href => {
    mediator.publish('router:navigate', href);
  }, []);
  return /*#__PURE__*/React.createElement(MainContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 296,
      columnNumber: 5
    }
  }, loading && /*#__PURE__*/React.createElement(LoadingModal, {
    "aria-label": "Loading",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 297,
      columnNumber: 19
    }
  }), /*#__PURE__*/React.createElement(InformationalScreen, {
    topNavigationProps: {
      animatedTitle: true,
      title: i18n.t(assistContentTitleKey),
      ...(!requiresConsentReview && {
        buttonStart: {
          element: isAccountProfile ? /*#__PURE__*/React.createElement(IconBack, {
            testID: "LibreAssist.close-Pressable",
            __self: this,
            __source: {
              fileName: _jsxFileName,
              lineNumber: 305,
              columnNumber: 17
            }
          }) : /*#__PURE__*/React.createElement(IconClose, {
            testID: "LibreAssist.close-Pressable",
            __self: this,
            __source: {
              fileName: _jsxFileName,
              lineNumber: 307,
              columnNumber: 17
            }
          }),
          onPress: () => mediator.publish('router:back')
        }
      })
    },
    testID: "LibreAssist",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 298,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Page, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 315,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(InformationalScreen.Content, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 316,
      columnNumber: 11
    }
  }, assistContentBodyTextKey && /*#__PURE__*/React.createElement(RichText, {
    testID: "LibreAssist.contentBodyText",
    onPressLink: handleContentLinkClick,
    text: assistContentBodyTextKey,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 318,
      columnNumber: 15
    }
  })), /*#__PURE__*/React.createElement(InformationalScreen.Footer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 325,
      columnNumber: 11
    }
  }, showConsentActions ? /*#__PURE__*/React.createElement(Stack, {
    alignSelf: "flex-end",
    width: "100%",
    gap: "$4",
    marginBottom: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 327,
      columnNumber: 15
    }
  }, /*#__PURE__*/React.createElement(Button, {
    onPress: () => handleSubmitAction('accept'),
    testID: "LibreAssist.accept",
    label: i18n.t(cmsConfig.acceptButtonKey),
    "aria-label": i18n.t(cmsConfig.acceptButtonKey),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 328,
      columnNumber: 17
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "tertiary",
    onPress: onDeclineButtonClick,
    testID: "LibreAssist.decline",
    label: i18n.t('Global.microcopy.common.decline'),
    "aria-label": i18n.t('Global.microcopy.common.decline'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 334,
      columnNumber: 17
    }
  })) : /*#__PURE__*/React.createElement(OptInOut, {
    optionStrings: {
      titleTag: 'Global.microcopy.common.status',
      positiveTag: cmsConfig.positiveTagKey,
      negativeTag: 'Global.microcopy.common.declined',
      positiveOption: cmsConfig.positiveOptionKey,
      negativeOption: 'Global.microcopy.common.decline'
    },
    initialState: Boolean((_auth$include$patient = auth.include.patient) === null || _auth$include$patient === void 0 || (_auth$include$patient = _auth$include$patient.consents) === null || _auth$include$patient === void 0 || (_auth$include$patient = _auth$include$patient.assist) === null || _auth$include$patient === void 0 ? void 0 : _auth$include$patient.accepted) ? 'accept' : 'decline',
    handleConsent: handleConsentWrapper,
    onDecline: onDeclineButtonClick,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 343,
      columnNumber: 15
    }
  })))));
};
export default connector(LibreAssistConsent);